import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, RefreshCw, Loader2, Youtube, Eye, EyeOff, Save, Link2, Tv } from "lucide-react";

const CATEGORIES = ["AI", "Robotics", "LLM", "Software"];

export const VideoAdmin = () => {
  const [videos, setVideos] = useState([]);
  const [settings, setSettings] = useState(null);
  const [url, setUrl] = useState("");
  const [cat, setCat] = useState("");
  const [channelInput, setChannelInput] = useState("");
  const [keywords, setKeywords] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.get("/admin/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/admin/video-settings").then((r) => { setSettings(r.data); setKeywords(r.data.keywords.join(", ")); }).catch(() => {});
  };
  useEffect(load, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) toast.success(typeof ok === "function" ? ok(r) : ok);
      load();
      return r;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Terjadi kesalahan");
    } finally {
      setBusy(false);
    }
  };

  const addVideo = (e) => {
    e.preventDefault();
    run(() => api.post("/admin/videos", { url, category: cat || null }), (r) => `Ditambahkan: ${r.data.title}`).then((r) => r && (setUrl(""), setCat("")));
  };
  const addChannel = (e) => {
    e.preventDefault();
    run(async () => {
      const r = await api.post("/admin/video-channels/resolve", { input: channelInput });
      if (settings.channels.some((c) => c.channel_id === r.data.channel_id)) throw Object.assign(new Error("Channel sudah ada"), { response: { data: { detail: "Channel sudah ada" } } });
      return api.put("/admin/video-settings", { channels: [...settings.channels, { ...r.data, enabled: true }] });
    }, "Channel ditambahkan").then((r) => r && setChannelInput(""));
  };
  const saveChannels = (channels) => run(() => api.put("/admin/video-settings", { channels }), "Daftar channel disimpan");
  const saveKeywords = (e) => {
    e.preventDefault();
    run(() => api.put("/admin/video-settings", { keywords: keywords.split(",").map((k) => k.trim()).filter(Boolean) }), "Kata kunci disimpan");
  };
  const sync = () => run(() => api.post("/admin/videos/sync"), (r) => `${r.data.inserted} video baru dari ${r.data.checked} yang dicek`);
  const update = (id, body, msg) => run(() => api.put(`/admin/videos/${id}`, body), msg);
  const remove = (id) => window.confirm("Hapus video ini?") && run(() => api.delete(`/admin/videos/${id}`), "Video dihapus");

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="video-admin">
      <div className="lg:col-span-1 space-y-6">
        <div className="card p-6" data-testid="video-add-card">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-2"><Link2 className="w-5 h-5 text-cyan1" /> Tambah Video Manual</h2>
          <p className="text-slate-500 text-sm mb-4">Tempel URL YouTube / Shorts / TikTok / Instagram Reels. Judul &amp; thumbnail diambil otomatis.</p>
          <form onSubmit={addVideo} className="space-y-3">
            <input required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.youtube.com/shorts/..." className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="video-url-input" />
            <select value={cat} onChange={(e) => setCat(e.target.value)} className="w-full bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="video-category-select">
              <option value="">Kategori otomatis</option>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button disabled={busy} className="btn-primary w-full justify-center" data-testid="video-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Tambah Video</button>
          </form>
        </div>

        <div className="card p-6" data-testid="video-channels-card">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-2"><Youtube className="w-5 h-5 text-magenta1" /> Channel YouTube Otomatis</h2>
          <p className="text-slate-500 text-sm mb-4">Video terbaru dari channel ini ditarik otomatis setiap siklus auto-update (tanpa API key).</p>
          {settings && (
            <>
              <label className="flex items-center gap-3 cursor-pointer mb-4">
                <input type="checkbox" checked={settings.auto_sync} onChange={(e) => run(() => api.put("/admin/video-settings", { auto_sync: e.target.checked }), e.target.checked ? "Sinkron otomatis aktif" : "Sinkron otomatis nonaktif")} className="w-4 h-4 accent-cyan-400" data-testid="video-autosync-toggle" />
                <span className="text-sm text-slate-300">Sinkron otomatis</span>
              </label>
              <div className="space-y-2 mb-4 max-h-64 overflow-y-auto" data-testid="video-channel-list">
                {settings.channels.map((ch) => (
                  <div key={ch.channel_id} className="flex items-center justify-between gap-2 text-sm border-b border-white/5 pb-2">
                    <label className="flex items-center gap-2 cursor-pointer min-w-0">
                      <input type="checkbox" checked={ch.enabled} onChange={(e) => saveChannels(settings.channels.map((c) => c.channel_id === ch.channel_id ? { ...c, enabled: e.target.checked } : c))} className="w-4 h-4 accent-cyan-400" data-testid={`channel-toggle-${ch.channel_id}`} />
                      <span className={`truncate ${ch.enabled ? "text-slate-200" : "text-slate-500 line-through"}`}>{ch.name || ch.channel_id}</span>
                    </label>
                    <button onClick={() => window.confirm(`Hapus channel ${ch.name}?`) && saveChannels(settings.channels.filter((c) => c.channel_id !== ch.channel_id))} className="text-slate-600 hover:text-red-400" data-testid={`channel-remove-${ch.channel_id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <form onSubmit={addChannel} className="flex gap-2 mb-4">
                <input required value={channelInput} onChange={(e) => setChannelInput(e.target.value)} placeholder="@handle, URL channel, atau UC..." className="flex-1 bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="channel-input" />
                <button disabled={busy} className="btn-ghost" data-testid="channel-add-btn"><Plus className="w-4 h-4" /></button>
              </form>
              <form onSubmit={saveKeywords} className="mb-4">
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Filter kata kunci (opsional, pisahkan koma)</label>
                <div className="flex gap-2 mt-1">
                  <input value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="ai, llm, github, robot" className="flex-1 bg-void border border-white/10 rounded-lg h-10 px-3 outline-none text-sm" data-testid="video-keywords-input" />
                  <button disabled={busy} className="btn-ghost" data-testid="video-keywords-save"><Save className="w-4 h-4" /></button>
                </div>
              </form>
              <button onClick={sync} disabled={busy} className="btn-primary w-full justify-center" data-testid="video-sync-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sinkron Sekarang</button>
              {settings.last_sync && <p className="text-[11px] font-mono text-slate-500 mt-3" data-testid="video-last-sync">Terakhir: {new Date(settings.last_sync).toLocaleString("id-ID")} · {settings.last_inserted ?? 0} baru</p>}
            </>
          )}
        </div>
      </div>

      <div className="lg:col-span-2 card p-6" data-testid="video-list-card">
        <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-4"><Tv className="w-5 h-5 text-cyan1" /> Semua Video <span className="chip">{videos.length}</span></h2>
        <div className="space-y-2 max-h-[70vh] overflow-y-auto">
          {videos.length === 0 && <p className="text-slate-600 text-sm">Belum ada video. Klik "Sinkron Sekarang" atau tambah manual.</p>}
          {videos.map((v) => (
            <div key={v.id} className={`flex items-start gap-3 border-b border-white/5 pb-3 ${v.visible ? "" : "opacity-50"}`} data-testid={`admin-video-${v.id}`}>
              {v.thumbnail && <img src={v.thumbnail} alt="" className="w-24 h-14 object-cover rounded-md shrink-0" />}
              <div className="flex-1 min-w-0">
                {editing?.id === v.id ? (
                  <form onSubmit={(e) => { e.preventDefault(); update(v.id, { title: editing.title, category: editing.category }, "Video diperbarui"); setEditing(null); }} className="flex flex-col sm:flex-row gap-2">
                    <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="flex-1 bg-void border border-white/10 rounded-lg h-9 px-3 outline-none text-sm" data-testid="video-edit-title" />
                    <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="bg-void border border-white/10 rounded-lg h-9 px-2 outline-none text-sm" data-testid="video-edit-category">
                      {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                    </select>
                    <button className="btn-primary !py-1 !px-3 text-xs" data-testid="video-edit-save">Simpan</button>
                    <button type="button" onClick={() => setEditing(null)} className="btn-ghost !py-1 !px-3 text-xs">Batal</button>
                  </form>
                ) : (
                  <>
                    <div className="text-sm text-slate-200 line-clamp-1 cursor-pointer hover:text-cyan1" onClick={() => setEditing({ id: v.id, title: v.title, category: v.category })} title="Klik untuk edit" data-testid={`video-title-${v.id}`}>{v.title}</div>
                    <div className="text-[11px] font-mono text-slate-500 mt-1 flex flex-wrap gap-2">
                      <span className="chip chip-cyan !text-[10px]">{v.category}</span>
                      <span>{v.channel_name}</span>
                      <span>· {v.platform}</span>
                      <span>· {v.source === "auto" ? "otomatis" : "manual"}</span>
                      <span>· {new Date(v.published_at).toLocaleDateString("id-ID")}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => update(v.id, { visible: !v.visible }, v.visible ? "Video disembunyikan" : "Video ditampilkan")} className="text-slate-500 hover:text-white p-1" title={v.visible ? "Sembunyikan" : "Tampilkan"} data-testid={`video-visible-${v.id}`}>{v.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                <button onClick={() => remove(v.id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`video-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
